import { Alert, Box, Button, Grid, Theme, Tooltip, Typography, useTheme } from "@mui/material";
import { Auth } from "aws-amplify";
import { useState } from "react";
import { FormikTextField } from "src/components/FormikComponents/FormikWrappers";
import { IRootState } from "src/redux/reducers/CombinedReducers";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import classNames from "classnames";
import { Form, Formik, FormikProps } from "formik"; //FormikHelpers
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

interface IPassword {
  verificationCode: string | null;
  newPassword: string | null;
  confirmPassword: string | null;
}
interface IChangePasswordProps {
  passwords: IPassword[] | null;
  className: string;
  authentication: any;
}

const ChangePasswordStyles = (theme: Theme): any => ({
  backgroundColor: theme.custom.timeline.contextListBackground,
  width: "100%",
  "& .form_footer": {
    padding: "10px",
  },
  "& .form_body": {
    width: "400px",
  },
  "& .required_fields_label": {
    padding: "10px",
  },
  "& .input_control label": {
    width: "120px",
  },
  "& .code_button, & .warning_wrapper": {
    margin: "32px",
    textAlign: "center",
  },
  "& .warning_wrapper": {
    display: "flex",
    alignItems: "center",
  },
});

export const ChangePasswordWindow = (props: IChangePasswordProps): JSX.Element => {
  const theme: Theme = useTheme();
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [currentView, setcurrentView] = useState<"requestCode" | "changePassword">("requestCode");
  const [newPasswordIsMasked, setNewPasswordIsMasked] = useState<boolean>(true);
  const [confirmPasswordIsMasked, setConfirmPasswordIsMasked] = useState<boolean>(true);
  const [delivery, setDelivery] = useState<any>(null);
  const [errorView, setError] = useState<boolean>(false);
  const { className } = props;
  const authentication = useSelector((state: IRootState) => state.authentication);

  const toggleNewPasswordMask = (): void => {
    setNewPasswordIsMasked((prevState: boolean) => !prevState);
  };
  const toggleConfirmPasswordMask = (): void => {
    setConfirmPasswordIsMasked((prevState: boolean) => !prevState);
  };

  const handleCodeRequest = (): void => {
    if (!Auth || typeof Auth.forgotPassword !== "function") {
      throw new Error("No Auth module found, please ensure @aws-amplify/auth is imported");
    }
    Auth.forgotPassword(authentication.attributes.email.toLowerCase())
      .then((data) => {
        setDelivery(data.CodeDeliveryDetails);
        setcurrentView("changePassword");
        setError(false);
      })
      .catch((err) => {
        setDelivery(null);
        setError(true);
      });
  };

  const handleLogout = async (): Promise<void> => {
    await Auth.signOut();
    window.location.replace("/");
  };

  const handleSubmit = (values: IPassword, actions: any): void => {
    Auth.forgotPasswordSubmit(authentication.attributes.email, values.verificationCode, values.confirmPassword)
      .then((data) => {
        handleLogout();
      })
      .catch((err) => {
        setDelivery(null);
      });
  };

  const errorBar = (): JSX.Element => {
    return (
      <Alert variant="outlined" severity="error">
        There was an issue sending your verification code. Please try again.
      </Alert>
    );
  };

  const changePassword = (): JSX.Element => {
    type ChangePasswordForm = FormikProps<IPassword>;
    const initialValues: IPassword = {
      verificationCode: null,
      newPassword: null,
      confirmPassword: null,
    };
    const changePasswordValidation = Yup.object().shape({
      verificationCode: Yup.string().required("Please enter the verification code that was sent to you").nullable(),
      newPassword: Yup.string().required("Please enter a new password").nullable(),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
        .required("Please ennter the password again")
        .nullable(),
    });

    return (
      <Formik
        className={className}
        validationSchema={changePasswordValidation}
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {(changePasswordForm: ChangePasswordForm) => {
          const { dirty, isValid, isSubmitting, handleReset } = changePasswordForm;
          return (
            <Form style={ChangePasswordStyles(theme)}>
              <Grid className="form_body" container direction="row">
                <Grid className={classNames("change_password input_fields", "grid_item_divider")}>
                  <Typography className="required_fields_label">Required Fields</Typography>
                  <Grid container className="required_fields">
                    <Grid container wrap="nowrap">
                      <FormikTextField label="Verification Code" name="verificationCode" type="text" />
                    </Grid>
                    <Grid container wrap="nowrap">
                      <FormikTextField
                        label="New Password"
                        name="newPassword"
                        type={newPasswordIsMasked ? "password" : "text"}
                      />
                      <Tooltip enterDelay={0} placement="top" title={newPasswordIsMasked ? "Reveal" : "Hide"}>
                        {newPasswordIsMasked ? (
                          <Visibility className="mask_toggle" onClick={toggleNewPasswordMask} />
                        ) : (
                          <VisibilityOff className="mask_toggle" onClick={toggleNewPasswordMask} />
                        )}
                      </Tooltip>
                    </Grid>
                    <Grid container wrap="nowrap">
                      <FormikTextField
                        label="Confirm Password"
                        name="confirmPassword"
                        type={confirmPasswordIsMasked ? "password" : "text"}
                      />
                      <Tooltip enterDelay={0} placement="top" title={confirmPasswordIsMasked ? "Reveal" : "Hide"}>
                        {confirmPasswordIsMasked ? (
                          <Visibility className="mask_toggle" onClick={toggleConfirmPasswordMask} />
                        ) : (
                          <VisibilityOff className="mask_toggle" onClick={toggleConfirmPasswordMask} />
                        )}
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container className="form_footer">
                  <Button
                    className="submit_btn btn"
                    type="submit"
                    variant="contained"
                    disabled={!(isValid && dirty) || isSubmitting}
                  >
                    Change Password
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    );
  };

  return (
    <Grid container sx={ChangePasswordStyles(theme)} justifyContent="center">
      <Grid item xs={6} className="warning_wrapper">
        <Grid container direction="column" alignItems="center" justifyContent="center" spacing={5}>
          <Box>
            <p>
              To reset your password, click "request code". A code will be sent to your email that you will need to
              perform a password change.
            </p>
          </Box>
          <Box>
            <Alert variant="outlined" severity="warning">
              Once your password has been changed you will be automatically logged out.
            </Alert>
          </Box>
          <Box>
            <p>
              <Button onClick={handleCodeRequest} variant="contained">
                Request Code
              </Button>
            </p>
          </Box>
        </Grid>
      </Grid>
      <Grid container justifyContent="center">
        <Grid item xs={4}>
          {errorView && currentView !== "changePassword" && errorBar()}
          {currentView === "changePassword" && changePassword()}
        </Grid>
      </Grid>
    </Grid>
  );
};
